import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    FormControlLabel,
    Slider,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getSettings, updateSettings } from '../api/api';

const PROVIDERS = [
    { value: 'deepseek-chat', label: 'DeepSeek' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Gemini' },
];

const MODEL_OPTIONS = {
    'deepseek-chat': [
        'deepseek-chat',
        'deepseek-v3',
        'deepseek-v3-0324',
        'deepseek-reasoner',
        'deepseek-r1-0528'
    ],
    'openai': [
        'gpt-4o',
        'gpt-4o-mini',
        'o1',
        'o1-mini',
        'o3',
        'o3-mini',
        'o4-mini',
        'gpt-4.1',
        'gpt-4.1-mini',
        'gpt-4.1-nano',
        'gpt-4-turbo',
        'gpt-4',
        'gpt-3.5-turbo'
    ],
    'anthropic': [
        'claude-opus-4',
        'claude-sonnet-4',
        'claude-3.7-sonnet',
        'claude-3.5-sonnet',
        'claude-3.5-haiku',
        'claude-3-opus',
        'claude-3-sonnet',
        'claude-3-haiku'
    ],
    'gemini': [
        'gemini-2.5-pro',
        'gemini-2.5-flash',
        'gemini-2.5-flash-lite',
        'gemini-2.0-flash',
        'gemini-2.0-flash-lite',
        'gemini-1.5-pro',
        'gemini-1.5-flash',
        'gemini-pro',
        'gemini-pro-vision'
    ],
};

function Settings() {
    const [settings, setSettings] = useState({
        // Global AI Settings (defaults for all prompts)
        ai_provider: '',
        ai_model: '',

        // Active Prompts (used by modern interface)
        filter_prompt: '',
        analysis_prompt: '',
        pricing_analysis_prompt: '',
        url_analysis_prompt: '',

        // Per-Prompt AI Provider Settings
        filter_prompt_provider: '',
        filter_prompt_model: '',
        analysis_prompt_provider: '',
        analysis_prompt_model: '',
        pricing_analysis_prompt_provider: '',
        pricing_analysis_prompt_model: '',
        url_analysis_prompt_provider: '',
        url_analysis_prompt_model: '',

        // Concurrent Settings
        concurrent_limit: 5,
        concurrent_kiddie: 2,
        concurrent_filter_gpt: 15,

        // Crawling Settings
        max_depth: 1,
        max_pages: 3,
        timeout: 10,
        max_retries: 1,
        retry_delay: 3,

        // Feature Toggles
        enable_separate_pricing: true,
        enable_pricing_focused_scraping: true,
        enable_url_analysis: true,

        // Pricing Settings
        pricing_timeout: 120,
        pricing_focused_max_pages: 8,
        url_analysis_timeout: 10,
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getSettings();
            setSettings(prev => ({ ...prev, ...data }));
        } catch (err) {
            setError('Failed to load settings: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess(false);
        try {
            await updateSettings(settings);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Failed to save settings: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const resetToDefaults = () => {
        if (window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
            loadSettings();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Settings
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={resetToDefaults}
                    >
                        Reset to Defaults
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={20} /> : 'Save All Settings'}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Settings saved successfully!
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* AI Provider Settings */}
                <Grid item xs={12}>
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Default AI Provider & Model</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Default AI provider and model used for all prompts. Individual prompts can override these settings below.
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Default Provider</InputLabel>
                                        <Select
                                            value={settings.ai_provider}
                                            label="Default Provider"
                                            onChange={(e) => handleChange('ai_provider', e.target.value)}
                                        >
                                            {PROVIDERS.map(p => (
                                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Default Model</InputLabel>
                                        <Select
                                            value={settings.ai_model}
                                            label="Default Model"
                                            onChange={(e) => handleChange('ai_model', e.target.value)}
                                        >
                                            {(MODEL_OPTIONS[settings.ai_provider] || []).map(m => (
                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* Concurrent Settings */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Concurrent Processing</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Typography gutterBottom>Main Concurrency Limit</Typography>
                                    <Slider
                                        value={settings.concurrent_limit}
                                        onChange={(_, value) => handleChange('concurrent_limit', value)}
                                        min={1}
                                        max={20}
                                        step={1}
                                        valueLabelDisplay="on"
                                        marks={[
                                            { value: 1, label: '1' },
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' },
                                            { value: 20, label: '20' }
                                        ]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography gutterBottom>Kiddie API Concurrency</Typography>
                                    <Slider
                                        value={settings.concurrent_kiddie}
                                        onChange={(_, value) => handleChange('concurrent_kiddie', value)}
                                        min={1}
                                        max={10}
                                        step={1}
                                        valueLabelDisplay="on"
                                        marks={[
                                            { value: 1, label: '1' },
                                            { value: 2, label: '2' },
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' }
                                        ]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography gutterBottom>GPT Filter Concurrency</Typography>
                                    <Slider
                                        value={settings.concurrent_filter_gpt}
                                        onChange={(_, value) => handleChange('concurrent_filter_gpt', value)}
                                        min={1}
                                        max={30}
                                        step={1}
                                        valueLabelDisplay="on"
                                        marks={[
                                            { value: 5, label: '5' },
                                            { value: 15, label: '15' },
                                            { value: 30, label: '30' }
                                        ]}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* Crawling Settings */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Website Crawling</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Max Crawling Depth"
                                        type="number"
                                        value={settings.max_depth}
                                        onChange={(e) => handleChange('max_depth', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 1, max: 5 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Max Pages per Website"
                                        type="number"
                                        value={settings.max_pages}
                                        onChange={(e) => handleChange('max_pages', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 1, max: 50 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Request Timeout (seconds)"
                                        type="number"
                                        value={settings.timeout}
                                        onChange={(e) => handleChange('timeout', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 5, max: 60 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Max Retries"
                                        type="number"
                                        value={settings.max_retries}
                                        onChange={(e) => handleChange('max_retries', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 0, max: 5 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Retry Delay (seconds)"
                                        type="number"
                                        value={settings.retry_delay}
                                        onChange={(e) => handleChange('retry_delay', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 1, max: 10 }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* Feature Toggles */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Feature Settings</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.enable_separate_pricing}
                                                onChange={(e) => handleChange('enable_separate_pricing', e.target.checked)}
                                            />
                                        }
                                        label="Enable Separate Pricing Analysis"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.enable_pricing_focused_scraping}
                                                onChange={(e) => handleChange('enable_pricing_focused_scraping', e.target.checked)}
                                            />
                                        }
                                        label="Enable Pricing-Focused Scraping"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.enable_url_analysis}
                                                onChange={(e) => handleChange('enable_url_analysis', e.target.checked)}
                                            />
                                        }
                                        label="Enable URL Analysis"
                                    />
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Pricing Analysis Timeout (seconds)"
                                        type="number"
                                        value={settings.pricing_timeout}
                                        onChange={(e) => handleChange('pricing_timeout', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 30, max: 300 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Pricing Max Pages"
                                        type="number"
                                        value={settings.pricing_focused_max_pages}
                                        onChange={(e) => handleChange('pricing_focused_max_pages', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 1, max: 20 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="URL Analysis Timeout (seconds)"
                                        type="number"
                                        value={settings.url_analysis_timeout}
                                        onChange={(e) => handleChange('url_analysis_timeout', parseInt(e.target.value))}
                                        fullWidth
                                        inputProps={{ min: 5, max: 30 }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* Prompts */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">AI Prompts (Modern Interface)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Configure prompts and their specific AI providers. Each prompt can use a different AI provider and model.
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Filter Prompt */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                            🔧 Filter Prompt
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Used by ALL analysis buttons for content filtering
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>AI Provider</InputLabel>
                                                    <Select
                                                        value={settings.filter_prompt_provider || settings.ai_provider}
                                                        label="AI Provider"
                                                        onChange={(e) => handleChange('filter_prompt_provider', e.target.value)}
                                                    >
                                                        {PROVIDERS.map(p => (
                                                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Model</InputLabel>
                                                    <Select
                                                        value={settings.filter_prompt_model || settings.ai_model}
                                                        label="Model"
                                                        onChange={(e) => handleChange('filter_prompt_model', e.target.value)}
                                                    >
                                                        {(MODEL_OPTIONS[settings.filter_prompt_provider || settings.ai_provider] || []).map(m => (
                                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Filter Prompt"
                                                    value={settings.filter_prompt}
                                                    onChange={(e) => handleChange('filter_prompt', e.target.value)}
                                                    multiline
                                                    minRows={3}
                                                    maxRows={6}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* URL Analysis Prompt */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                            🔗 URL Analysis Prompt
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Used by ALL analysis buttons for URL relevance checking
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>AI Provider</InputLabel>
                                                    <Select
                                                        value={settings.url_analysis_prompt_provider || settings.ai_provider}
                                                        label="AI Provider"
                                                        onChange={(e) => handleChange('url_analysis_prompt_provider', e.target.value)}
                                                    >
                                                        {PROVIDERS.map(p => (
                                                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Model</InputLabel>
                                                    <Select
                                                        value={settings.url_analysis_prompt_model || settings.ai_model}
                                                        label="Model"
                                                        onChange={(e) => handleChange('url_analysis_prompt_model', e.target.value)}
                                                    >
                                                        {(MODEL_OPTIONS[settings.url_analysis_prompt_provider || settings.ai_provider] || []).map(m => (
                                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="URL Analysis Prompt"
                                                    value={settings.url_analysis_prompt}
                                                    onChange={(e) => handleChange('url_analysis_prompt', e.target.value)}
                                                    multiline
                                                    minRows={3}
                                                    maxRows={6}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Analysis Prompt */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'success.main' }}>
                                            📊 Analysis Prompt
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Used by Description button and Run Analysis dialog
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>AI Provider</InputLabel>
                                                    <Select
                                                        value={settings.analysis_prompt_provider || settings.ai_provider}
                                                        label="AI Provider"
                                                        onChange={(e) => handleChange('analysis_prompt_provider', e.target.value)}
                                                    >
                                                        {PROVIDERS.map(p => (
                                                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Model</InputLabel>
                                                    <Select
                                                        value={settings.analysis_prompt_model || settings.ai_model}
                                                        label="Model"
                                                        onChange={(e) => handleChange('analysis_prompt_model', e.target.value)}
                                                    >
                                                        {(MODEL_OPTIONS[settings.analysis_prompt_provider || settings.ai_provider] || []).map(m => (
                                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Analysis Prompt"
                                                    value={settings.analysis_prompt}
                                                    onChange={(e) => handleChange('analysis_prompt', e.target.value)}
                                                    multiline
                                                    minRows={4}
                                                    maxRows={8}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Pricing Analysis Prompt */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'success.main' }}>
                                            💰 Pricing Analysis Prompt
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Used by Pricing button and Run Analysis dialog
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>AI Provider</InputLabel>
                                                    <Select
                                                        value={settings.pricing_analysis_prompt_provider || settings.ai_provider}
                                                        label="AI Provider"
                                                        onChange={(e) => handleChange('pricing_analysis_prompt_provider', e.target.value)}
                                                    >
                                                        {PROVIDERS.map(p => (
                                                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Model</InputLabel>
                                                    <Select
                                                        value={settings.pricing_analysis_prompt_model || settings.ai_model}
                                                        label="Model"
                                                        onChange={(e) => handleChange('pricing_analysis_prompt_model', e.target.value)}
                                                    >
                                                        {(MODEL_OPTIONS[settings.pricing_analysis_prompt_provider || settings.ai_provider] || []).map(m => (
                                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Pricing Analysis Prompt"
                                                    value={settings.pricing_analysis_prompt}
                                                    onChange={(e) => handleChange('pricing_analysis_prompt', e.target.value)}
                                                    multiline
                                                    minRows={3}
                                                    maxRows={6}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Settings; 